/**
 * ArtistProfilePage
 * Public profile for an artist: every approved drawing under their name, with
 * heart counts. Reached by tapping a verified name in the gallery. Verified
 * artists also get a "get the app" nudge here (where it has the most context).
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, BadgeCheck, ArrowLeft, Globe } from 'lucide-react';
import { formatPromptNumber } from '@/utils/format';
import { getArtistProfile, type ArtistProfile } from '@/services/supabase-storage';
import { APP_STORE_URL } from '@/config/appStore';

const MONO = { fontFamily: 'FK Grotesk Mono, monospace' } as const;
const DISPLAY = { fontFamily: 'Delcan Mono, monospace' } as const;

export function ArtistProfilePage() {
  const { alias = '' } = useParams();
  const [profile, setProfile] = useState<ArtistProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getArtistProfile(alias).then((p) => {
      if (!cancelled) {
        setProfile(p);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [alias]);

  return (
    <div className="h-screen overflow-y-auto">
      <div className="container mx-auto px-4 py-12">
        <Link
          to="/gallery"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          style={{ ...MONO, fontSize: '10pt', letterSpacing: '0.05em' }}
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> BACK TO GALLERY
        </Link>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : !profile || profile.count === 0 ? (
          <div className="text-muted-foreground text-center py-24" style={MONO}>
            No drawings found for &quot;{decodeURIComponent(alias)}&quot;.
          </div>
        ) : (
          <>
            <header className="mb-10">
              {profile.avatar && (
                <img
                  src={profile.avatar}
                  alt={`${profile.alias} avatar`}
                  className="w-20 h-20 rounded-full border border-border object-cover bg-muted/10 mb-4"
                />
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-foreground text-4xl leading-tight" style={DISPLAY}>
                  {profile.alias}
                </h1>
                {profile.verified && (
                  <BadgeCheck className="w-6 h-6 text-primary" aria-label="Verified artist" />
                )}
              </div>
              <p className="text-muted-foreground mt-2 uppercase" style={{ ...MONO, fontSize: '10pt', letterSpacing: '0.1em' }}>
                {profile.count} {profile.count === 1 ? 'drawing' : 'drawings'}
                {profile.totalHearts > 0 ? ` · ${profile.totalHearts} ${profile.totalHearts === 1 ? 'mark' : 'marks'}` : ''}
              </p>
              {profile.bio && (
                <p className="text-foreground mt-4 max-w-xl whitespace-pre-line" style={{ ...MONO, fontSize: '11pt', lineHeight: 1.6 }}>
                  {profile.bio}
                </p>
              )}
              {profile.website && (
                <a
                  href={profile.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-muted-foreground hover:text-foreground transition-colors"
                  style={{ ...MONO, fontSize: '10pt' }}
                >
                  <Globe className="w-3.5 h-3.5" aria-hidden="true" />
                  {profile.website.replace(/^https?:\/\//i, '').replace(/\/$/, '')}
                </a>
              )}
              {profile.verified && (
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full border border-border text-foreground hover:bg-foreground hover:text-background transition-colors"
                  style={{ ...MONO, fontSize: '10pt' }}
                >
                  <BadgeCheck className="w-4 h-4" aria-hidden="true" />
                  Verified on the Prompt-Brush app — get it →
                </a>
              )}
            </header>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {profile.artworks.map((a) => (
                <div key={a.id}>
                  <img
                    src={a.image || a.imageData}
                    alt={a.promptText || `Drawing ${formatPromptNumber(a.promptNumber)}`}
                    className="w-full h-auto rounded border border-border bg-muted/10"
                    style={{ aspectRatio: '500/700' }}
                    loading="lazy"
                    decoding="async"
                  />
                  <div
                    className="text-foreground text-lg mt-2 line-clamp-2 leading-snug"
                    style={DISPLAY}
                    title={a.promptText || ''}
                  >
                    {a.promptText || `Prompt ${formatPromptNumber(a.promptNumber)}`}
                  </div>
                  <div
                    className="flex items-center gap-3 mt-1 text-muted-foreground"
                    style={{ ...MONO, fontSize: '9pt' }}
                  >
                    <span>{formatPromptNumber(a.promptNumber)}</span>
                    {a.heartCount > 0 && (
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="inline-block rounded-full bg-current text-foreground"
                          style={{ width: 10, height: 10 }}
                          aria-hidden="true"
                        />
                        {a.heartCount}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ArtistProfilePage;
